import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { LogOut, Plus, Pencil, Trash2, Save, X, Eye, MessageSquare, BarChart3, TrendingUp, Users, FileText } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPanel() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Blog state
  const [posts, setPosts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('analytics');
  const [editingPost, setEditingPost] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title_de: '', title_en: '', summary: '', image_url: '' });

  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('enbet_admin_token');
      if (!token) { setLoading(false); return; }
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(res.data);
    } catch {
      localStorage.removeItem('enbet_admin_token');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem('enbet_admin_token', res.data.token);
      setUser(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setLoginError(typeof detail === 'string' ? detail : 'Login fehlgeschlagen');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('enbet_admin_token');
    setUser(null);
  };

  const getHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('enbet_admin_token')}` }
  });

  // Load data when authenticated
  useEffect(() => {
    if (user) {
      fetchPosts();
      fetchContacts();
      fetchAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API}/blog`);
      setPosts(res.data);
    } catch {}
  };

  const fetchContacts = async () => {
    try {
      const res = await axios.get(`${API}/contacts`, getHeaders());
      setContacts(res.data);
    } catch {}
  };

  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/analytics/stats`, getHeaders());
      setAnalytics(res.data);
    } catch {}
  };

  const handleSavePost = async (e) => {
    e.preventDefault();
    try {
      if (editingPost) {
        await axios.put(`${API}/blog/${editingPost.id}`, form, getHeaders());
      } else {
        await axios.post(`${API}/blog`, form, getHeaders());
      }
      setShowForm(false);
      setEditingPost(null);
      setForm({ title_de: '', title_en: '', summary: '', image_url: '' });
      fetchPosts();
    } catch {}
  };

  const handleDeletePost = async (id) => {
    if (!window.confirm('Beitrag wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/blog/${id}`, getHeaders());
      fetchPosts();
    } catch {}
  };

  const startEdit = (post) => {
    setEditingPost(post);
    setForm({ title_de: post.title_de, title_en: post.title_en, summary: post.summary, image_url: post.image_url || '' });
    setShowForm(true);
  };

  const startNew = () => {
    setEditingPost(null);
    setForm({ title_de: '', title_en: '', summary: '', image_url: '' });
    setShowForm(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-white/50 text-sm animate-pulse" style={{ fontFamily: "'Oswald', sans-serif" }}>LOADING...</div>
      </div>
    );
  }

  // Login Screen
  if (!user) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <img src="/assets/logo.png" alt="ENBET" className="h-12 mx-auto mb-10" />
          <h1
            className="text-white text-3xl font-bold uppercase tracking-tight text-center mb-8"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            Admin Login
          </h1>

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="text-white/40 text-xs uppercase tracking-[0.15em] mb-2 block" style={{ fontFamily: "'Oswald', sans-serif" }}>
                E-Mail
              </label>
              <Input
                data-testid="admin-email-input"
                type="email"
                name="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-transparent border-[#333] text-white rounded-none focus:border-[#befa0a] h-12"
              />
            </div>
            <div>
              <label className="text-white/40 text-xs uppercase tracking-[0.15em] mb-2 block" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Passwort
              </label>
              <Input
                data-testid="admin-password-input"
                type="password"
                name="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-transparent border-[#333] text-white rounded-none focus:border-[#befa0a] h-12"
              />
            </div>

            {loginError && (
              <p data-testid="admin-login-error" className="text-red-400 text-sm">{loginError}</p>
            )}

            <button
              data-testid="admin-login-btn"
              type="submit"
              disabled={loginLoading}
              className="w-full bg-[#befa0a] text-[#0a0a0a] py-3.5 text-sm font-bold uppercase tracking-[0.15em] hover:bg-white transition-colors disabled:opacity-50"
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              {loginLoading ? 'Anmelden...' : 'Anmelden'}
            </button>
          </form>

          <button
            data-testid="admin-back-home"
            onClick={() => navigate('/')}
            className="block mx-auto mt-8 text-white/30 text-xs uppercase tracking-[0.15em] hover:text-[#befa0a] transition-colors"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            Zurück zur Website
          </button>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="border-b border-[#1a1a1a] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img src="/assets/logo.png" alt="ENBET" className="h-8" />
          <span className="text-white/30 text-xs uppercase tracking-[0.15em]" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Admin Panel
          </span>
        </div>
        <div className="flex items-center gap-4">
          <button
            data-testid="admin-view-site"
            onClick={() => navigate('/')}
            className="text-white/50 text-xs uppercase tracking-[0.15em] hover:text-[#befa0a] transition-colors flex items-center gap-2"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            <Eye size={14} /> Website
          </button>
          <button
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            className="text-white/50 text-xs uppercase tracking-[0.15em] hover:text-red-400 transition-colors flex items-center gap-2"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 py-10">
        {/* Tabs */}
        <div className="flex gap-1 mb-10 border-b border-[#1a1a1a]">
          {[
            { key: 'analytics', label: 'Analytics', icon: <BarChart3 size={14} /> },
            { key: 'posts', label: 'Blog-Beiträge', icon: <Pencil size={14} /> },
            { key: 'contacts', label: 'Anfragen', icon: <MessageSquare size={14} /> },
          ].map((tab) => (
            <button
              key={tab.key}
              data-testid={`admin-tab-${tab.key}`}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-[0.15em] border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-[#befa0a] text-[#befa0a]'
                  : 'border-transparent text-white/40 hover:text-white/70'
              }`}
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Blog Posts Tab */}
        {activeTab === 'posts' && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-white text-2xl font-bold uppercase tracking-tight" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Blog-Beiträge ({posts.length})
              </h2>
              <button
                data-testid="admin-new-post-btn"
                onClick={startNew}
                className="bg-[#befa0a] text-[#0a0a0a] px-5 py-2.5 text-xs font-bold uppercase tracking-[0.15em] hover:bg-white transition-colors flex items-center gap-2"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                <Plus size={14} /> Neuer Beitrag
              </button>
            </div>

            {/* Post Form */}
            {showForm && (
              <div data-testid="admin-post-form" className="border border-[#333] p-6 mb-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-white text-sm uppercase tracking-[0.15em]" style={{ fontFamily: "'Oswald', sans-serif" }}>
                    {editingPost ? 'Beitrag bearbeiten' : 'Neuer Beitrag'}
                  </h3>
                  <button onClick={() => { setShowForm(false); setEditingPost(null); }} className="text-white/40 hover:text-white">
                    <X size={18} />
                  </button>
                </div>
                <form onSubmit={handleSavePost} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-white/40 text-xs uppercase tracking-[0.15em] mb-1 block" style={{ fontFamily: "'Oswald', sans-serif" }}>Titel (DE)</label>
                      <Input
                        data-testid="admin-post-title-de"
                        required value={form.title_de}
                        onChange={(e) => setForm({ ...form, title_de: e.target.value })}
                        className="bg-transparent border-[#333] text-white rounded-none focus:border-[#befa0a] h-10"
                      />
                    </div>
                    <div>
                      <label className="text-white/40 text-xs uppercase tracking-[0.15em] mb-1 block" style={{ fontFamily: "'Oswald', sans-serif" }}>Titel (EN)</label>
                      <Input
                        data-testid="admin-post-title-en"
                        required value={form.title_en}
                        onChange={(e) => setForm({ ...form, title_en: e.target.value })}
                        className="bg-transparent border-[#333] text-white rounded-none focus:border-[#befa0a] h-10"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-white/40 text-xs uppercase tracking-[0.15em] mb-1 block" style={{ fontFamily: "'Oswald', sans-serif" }}>Zusammenfassung</label>
                    <Textarea
                      data-testid="admin-post-summary"
                      required rows={3} value={form.summary}
                      onChange={(e) => setForm({ ...form, summary: e.target.value })}
                      className="bg-transparent border-[#333] text-white rounded-none focus:border-[#befa0a] resize-none"
                    />
                  </div>
                  <div>
                    <label className="text-white/40 text-xs uppercase tracking-[0.15em] mb-1 block" style={{ fontFamily: "'Oswald', sans-serif" }}>Bild-URL (optional)</label>
                    <Input
                      data-testid="admin-post-image"
                      value={form.image_url}
                      onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                      className="bg-transparent border-[#333] text-white rounded-none focus:border-[#befa0a] h-10"
                    />
                  </div>
                  <button
                    data-testid="admin-save-post-btn"
                    type="submit"
                    className="bg-[#befa0a] text-[#0a0a0a] px-6 py-2.5 text-xs font-bold uppercase tracking-[0.15em] hover:bg-white transition-colors flex items-center gap-2"
                    style={{ fontFamily: "'Oswald', sans-serif" }}
                  >
                    <Save size={14} /> Speichern
                  </button>
                </form>
              </div>
            )}

            {/* Posts List */}
            <div className="space-y-3">
              {posts.map((post) => (
                <div
                  key={post.id}
                  data-testid={`admin-post-${post.id}`}
                  className="border border-[#1a1a1a] p-5 flex items-start justify-between gap-4 hover:border-[#333] transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <h4 className="text-white text-sm font-bold truncate" style={{ fontFamily: "'Oswald', sans-serif" }}>
                      {post.title_de}
                    </h4>
                    <p className="text-white/40 text-xs mt-1 truncate" style={{ fontFamily: "'Manrope', sans-serif" }}>
                      {post.summary}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      data-testid={`admin-edit-${post.id}`}
                      onClick={() => startEdit(post)}
                      className="text-white/40 hover:text-[#befa0a] transition-colors p-2"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      data-testid={`admin-delete-${post.id}`}
                      onClick={() => handleDeletePost(post.id)}
                      className="text-white/40 hover:text-red-400 transition-colors p-2"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
              {posts.length === 0 && (
                <p className="text-white/30 text-sm text-center py-10">Keine Beiträge vorhanden</p>
              )}
            </div>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div>
            <h2 className="text-white text-2xl font-bold uppercase tracking-tight mb-8" style={{ fontFamily: "'Oswald', sans-serif" }}>
              Anfragen ({contacts.length})
            </h2>
            <div className="space-y-3">
              {contacts.map((c, i) => (
                <div key={i} data-testid={`admin-contact-${i}`} className="border border-[#1a1a1a] p-5 hover:border-[#333] transition-colors">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <span className="text-white text-sm font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>{c.name}</span>
                      <span className="text-white/40 text-xs ml-3">{c.email}</span>
                      {c.company && <span className="text-white/30 text-xs ml-3">{c.company}</span>}
                    </div>
                    <span className="text-white/20 text-xs flex-shrink-0">
                      {c.created_at ? new Date(c.created_at).toLocaleDateString('de-DE') : ''}
                    </span>
                  </div>
                  <p className="text-white/60 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>{c.message}</p>
                </div>
              ))}
              {contacts.length === 0 && (
                <p className="text-white/30 text-sm text-center py-10">Keine Anfragen vorhanden</p>
              )}
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div data-testid="admin-analytics-tab">
            <h2 className="text-white text-2xl font-bold uppercase tracking-tight mb-8" style={{ fontFamily: "'Oswald', sans-serif" }}>
              Analytics
            </h2>

            {analytics ? (
              <>
                {/* Stat Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                  {[
                    { label: 'Heute', value: analytics.today_views, icon: <TrendingUp size={18} /> },
                    { label: '7 Tage', value: analytics.week_views, icon: <BarChart3 size={18} /> },
                    { label: '30 Tage', value: analytics.month_views, icon: <BarChart3 size={18} /> },
                    { label: 'Gesamt', value: analytics.total_views, icon: <Eye size={18} /> },
                  ].map((stat, i) => (
                    <div key={i} data-testid={`analytics-stat-${i}`} className="border border-[#1a1a1a] p-5">
                      <div className="flex items-center gap-2 text-[#befa0a] mb-3">{stat.icon}</div>
                      <p className="text-white text-3xl font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>{stat.value}</p>
                      <p className="text-white/40 text-xs uppercase tracking-[0.15em] mt-1" style={{ fontFamily: "'Oswald', sans-serif" }}>{stat.label}</p>
                    </div>
                  ))}
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
                  <div className="border border-[#1a1a1a] p-5">
                    <div className="flex items-center gap-2 text-[#befa0a] mb-2"><Users size={16} /></div>
                    <p className="text-white text-2xl font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>{analytics.total_contacts}</p>
                    <p className="text-white/40 text-xs uppercase tracking-[0.15em] mt-1" style={{ fontFamily: "'Oswald', sans-serif" }}>Kontaktanfragen</p>
                  </div>
                  <div className="border border-[#1a1a1a] p-5">
                    <div className="flex items-center gap-2 text-[#befa0a] mb-2"><FileText size={16} /></div>
                    <p className="text-white text-2xl font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>{analytics.total_posts}</p>
                    <p className="text-white/40 text-xs uppercase tracking-[0.15em] mt-1" style={{ fontFamily: "'Oswald', sans-serif" }}>Blog-Beiträge</p>
                  </div>
                </div>

                {/* Daily Chart */}
                <div className="border border-[#1a1a1a] p-6 mb-10">
                  <h3 className="text-white text-sm uppercase tracking-[0.15em] mb-6" style={{ fontFamily: "'Oswald', sans-serif" }}>
                    Seitenaufrufe (letzte 7 Tage)
                  </h3>
                  <div className="flex items-end gap-2 h-40">
                    {analytics.daily.map((day, i) => {
                      const maxVal = Math.max(...analytics.daily.map(d => d.views), 1);
                      const heightPct = (day.views / maxVal) * 100;
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-2">
                          <span className="text-white/60 text-xs" style={{ fontFamily: "'Manrope', sans-serif" }}>{day.views}</span>
                          <div className="w-full flex-1 flex items-end">
                            <div
                              className="w-full bg-[#befa0a]/80 hover:bg-[#befa0a] transition-colors"
                              style={{ height: `${Math.max(heightPct, 4)}%` }}
                            />
                          </div>
                          <span className="text-white/30 text-[10px]" style={{ fontFamily: "'Manrope', sans-serif" }}>{day.date}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Top Pages */}
                {analytics.pages.length > 0 && (
                  <div className="border border-[#1a1a1a] p-6">
                    <h3 className="text-white text-sm uppercase tracking-[0.15em] mb-6" style={{ fontFamily: "'Oswald', sans-serif" }}>
                      Top Seiten
                    </h3>
                    <div className="space-y-3">
                      {analytics.pages.map((p, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <span className="text-white/70 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>{p.page}</span>
                          <span className="text-[#befa0a] text-sm font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>{p.views}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-white/30 text-sm text-center py-10 animate-pulse">Daten werden geladen...</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
